import React, { useCallback } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import { formatOptions, FormatType, MetricType, type ReviewCellRendererProps } from '../helpers';
import { Icon, type OptionType, TriggerableMenu } from 'venn-ui-kit';
import { Numbers, replaceArrayItem } from 'venn-utils';
import { cn } from '@/lib/utils';

export const FormatAndPerformanceCellRenderer = ({
  additionalMetadata,
  currentMapping,
  data,
  onMappingChange,
}: ICellRendererParams & ReviewCellRendererProps) => {
  const { annualizedVolatility, newDataCount, periodAnnualized, periodReturn } = data;
  const { isDeleted, isNew } = additionalMetadata.perSeries[data.seriesId]!;
  const [returnPercentageOption, returnDecimalOption, priceOption] = formatOptions;
  const selectedFormatOptionLabel =
    data.metricId === MetricType.Return
      ? data.isPercent
        ? returnPercentageOption.label
        : returnDecimalOption.label
      : priceOption.label;

  const onSelectFormat = useCallback(
    (selectedOption: OptionType<FormatType>) => {
      let metricId;
      let isPercent;

      const columnToUpdateIndex = currentMapping.columns.findIndex((c) => c.seriesId === data.seriesId);

      if (selectedOption.value === FormatType.ReturnPercentage) {
        metricId = MetricType.Return;
        isPercent = true;
      } else if (selectedOption.value === FormatType.ReturnDecimal) {
        metricId = MetricType.Return;
        isPercent = false;
      } else {
        metricId = MetricType.Price;
        isPercent = data.isPercent;
      }

      const updatedColumn = {
        ...data,
        isPercent,
        metricId,
      };

      const newMapping = {
        ...currentMapping,
        columns: replaceArrayItem(currentMapping.columns, columnToUpdateIndex, updatedColumn),
      };

      onMappingChange(newMapping);
    },
    [currentMapping, data, onMappingChange],
  );

  return (
    <div className="text-[12px] text-venn-grey-80 leading-[16px]">
      <div>
        <TriggerableMenu
          usePortal
          options={formatOptions}
          className="!min-h-[16px] font-bold text-venn-dark-blue cursor-pointer"
          innerClassName="mt-[19px]"
          onSelect={onSelectFormat}
        >
          {selectedFormatOptionLabel ?? '--'}
          <Icon type="chevron-down" className="ml-[4px]" />
        </TriggerableMenu>
      </div>
      <div>
        <span
          className={cn([
            'text-venn-black font-bold',
            isNew && 'text-venn-highlight-dark',
            (isDeleted || newDataCount === 0) && 'text-venn-grey-60',
          ])}
        >
          +{newDataCount}
        </span>{' '}
        Observations
      </div>
      <div>
        {periodAnnualized ? 'Ann. ' : 'Cum. '}Ret.
        <span className="text-venn-black font-bold">
          {' '}
          {periodReturn ? Numbers.safeFormatPercentage(periodReturn) : '--'}
        </span>
      </div>
      <div>
        {periodAnnualized ? 'Ann. ' : ''}Vol.
        <span className="text-venn-black font-bold">
          {' '}
          {periodAnnualized && annualizedVolatility ? Numbers.safeFormatPercentage(annualizedVolatility) : '--'}
        </span>
      </div>
    </div>
  );
};
